/**
 * ProductGrid Component
 *
 * Responsive flex-wrap grid of product cards matching Shapermint's design:
 * - Flex-wrap layout (3-col desktop, 2-col mobile) with 30px gap
 * - Badges overlaid on the image (top-left for sale, bottom for best-seller)
 * - Center-aligned product name
 * - Strikethrough compare price + bold sale price with superscript cents
 * - Star rating row with review count
 * - Rounded 8px image corners with hover zoom
 */

import React, { useState, useCallback } from 'react';
import styled from '@emotion/styled';
import { Link } from 'react-router-dom';
import { PackageSearch } from 'lucide-react';
import { CollectionProduct } from '../../../domain/entities/collection.entity';
import { useIntl } from '@/lib/intl';
import { ColorSelector } from '../../atoms/ColorSelector';

/* ---------- grid (flex-wrap matching Shapermint) ------------------------- */

const Grid = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 30px;
  padding: 0 10px;

  @media (max-width: 480px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    padding: 0;
  }
`;

/* ---------- card wrapper ------------------------------------------------- */

const CardLink = styled(Link)`
  display: flex;
  flex-direction: column;
  text-decoration: none;
  color: inherit;
  width: calc(33.333% - 20px);

  &:hover img {
    transform: scale(1.04);
  }

  @media (max-width: 900px) {
    width: calc(50% - 15px);
  }

  @media (max-width: 480px) {
    width: 100%;
  }
`;

/* ---------- image area --------------------------------------------------- */

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 3 / 4;
  border-radius: 8px;
  overflow: hidden;
  background: #f8f8f8;
`;

const ProductImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.3s ease;
`;

const PlaceholderImage = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ccc;

  svg {
    width: 48px;
    height: 48px;
  }
`;

/* ---------- image overlay badges ----------------------------------------- */

const TopBadge = styled.span`
  position: absolute;
  top: 10px;
  left: 0;
  z-index: 2;
  padding: 4px 10px;
  border-radius: 0 4px 4px 0;
  font-size: 12px;
  font-weight: 700;
  text-transform: capitalize;
  background: var(--colors-semantic-urgent-color, #d9534f);
  color: #fff;
  letter-spacing: 0.3px;

  @media (max-width: 768px) {
    font-size: 10px;
    padding: 3px 8px;
  }
`;

const BottomBadge = styled.span`
  position: absolute;
  bottom: 10px;
  left: 0;
  z-index: 2;
  padding: 4px 12px;
  border-radius: 0 4px 4px 0;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  background: rgba(255, 255, 255, 0.95);
  color: #333;
  white-space: nowrap;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    font-size: 9px;
    padding: 3px 8px;
  }
`;

/* ---------- color selector ----------------------------------------------- */

const ColorSelectorRow = styled.div`
  display: flex;
  justify-content: center;
  padding: 6px 0 0;
`;

/* ---------- content below image ------------------------------------------ */

const ContentArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 10px 0 12px;

  @media (max-width: 768px) {
    padding: 8px 0 10px;
  }
`;

const ProductName = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #292929;
  line-height: 1.35;
  margin: 0;
  text-align: center;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  width: 100%;
  max-height: 2.75rem;

  @media (max-width: 768px) {
    font-size: 12px;
    font-weight: 500;
  }
`;

/* ---------- price row ---------------------------------------------------- */

const PriceRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  flex-wrap: wrap;
`;

const ComparePrice = styled.span`
  font-size: 14px;
  color: #888;
  text-decoration: line-through;
  font-weight: 400;

  @media (max-width: 480px) {
    font-size: 11px;
  }
`;

const SalePrice = styled.span<{ $hasCompare?: boolean }>`
  font-size: 20px;
  font-weight: 700;
  color: ${({ $hasCompare }) =>
    $hasCompare ? 'var(--colors-pallete-secondary-color, #d9534f)' : '#292929'};
  display: inline-flex;
  align-items: baseline;
  line-height: 1;

  sup {
    font-size: 0.6em;
    position: relative;
    top: -0.45em;
  }

  @media (max-width: 480px) {
    font-size: 16px;
  }
`;

/* ---------- BNPL row ----------------------------------------------------- */

const BNPLRow = styled.div`
  font-size: 12px;
  color: #666;
  text-align: center;
`;

/* ---------- rating ------------------------------------------------------- */

const RatingRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
`;

const StarsRow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 1px;
  line-height: 1;
`;

const ReviewCount = styled.span`
  font-size: 12px;
  color: #666;
`;

/* ---------- empty & loading ---------------------------------------------- */

const EmptyState = styled.div`
  width: 100%;
  text-align: center;
  padding: 48px 24px;
  color: #6b7280;
`;

const EmptyIcon = styled.div`
  margin-bottom: 16px;
  color: #9ca3af;
`;

const EmptyTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #374151;
  margin: 0 0 8px 0;
`;

const EmptyDescription = styled.p`
  font-size: 14px;
  margin: 0;
`;

const SkeletonPulse = styled.div`
  background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s ease-in-out infinite;
  border-radius: 4px;

  @keyframes shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
`;

const SkeletonCard = styled.div`
  width: calc(33.333% - 20px);

  @media (max-width: 900px) {
    width: calc(50% - 15px);
  }

  @media (max-width: 480px) {
    width: 100%;
  }
`;

/* ---------- helpers ------------------------------------------------------ */

const SALE_TAGS = new Set(['sale', 'on-sale', 'clearance', 'spring-sale', 'summer-sale']);
const BEST_SELLER_TAGS = new Set(['best-sellers', 'bestseller', 'best-seller']);
const SELLING_FAST_TAGS = new Set(['selling-fast']);

function getTopBadgeLabel(tags: string[]): string | null {
  for (const tag of tags) {
    const t = tag.toLowerCase();
    if (SALE_TAGS.has(t)) return tag.replaceAll('-', ' ');
    if (SELLING_FAST_TAGS.has(t)) return 'Selling Fast';
  }
  return null;
}

function getBottomBadgeLabel(tags: string[]): string | null {
  for (const tag of tags) {
    if (BEST_SELLER_TAGS.has(tag.toLowerCase())) return 'Best Seller';
  }
  return null;
}

function splitPrice(amount: number): { dollars: string; cents: string } {
  const dollars = Math.floor(amount);
  const cents = Math.round((amount - dollars) * 100);
  return {
    dollars: dollars.toString(),
    cents: cents.toString().padStart(2, '0'),
  };
}

const STAR_PATH =
  'M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z';
const STAR_FILL = '#f5a623';
const STAR_EMPTY = '#d1d5db';

let gridHalfStarId = 0;

function StarIcons({ rating }: Readonly<{ rating: number }>) {
  const full = Math.floor(rating);
  const hasHalf = rating - full >= 0.25;
  const empty = 5 - full - (hasHalf ? 1 : 0);
  const gradientId = `grid-half-${gridHalfStarId++}`;

  return (
    <>
      {Array.from({ length: full }, (_, i) => (
        <svg key={`f${i}`} width="14" height="14" viewBox="0 0 24 24">
          <path d={STAR_PATH} fill={STAR_FILL} />
        </svg>
      ))}
      {hasHalf && (
        <svg key="h" width="14" height="14" viewBox="0 0 24 24">
          <defs>
            <linearGradient id={gradientId}>
              <stop offset="50%" stopColor={STAR_FILL} />
              <stop offset="50%" stopColor={STAR_EMPTY} />
            </linearGradient>
          </defs>
          <path d={STAR_PATH} fill={`url(#${gradientId})`} />
        </svg>
      )}
      {Array.from({ length: empty }, (_, i) => (
        <svg key={`e${i}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={STAR_EMPTY} strokeWidth="1.5">
          <path d={STAR_PATH} />
        </svg>
      ))}
    </>
  );
}

function getInstallmentPrice(price: number, installments: number): string {
  return (price / installments).toFixed(2);
}

const STANDARD_RATING = 4.5;
const STANDARD_REVIEW_COUNT = 1200;

/* ---------- single product card (stateful for color selection) ----------- */

const MOBILE_MAX_COLORS = 4;
const DESKTOP_MAX_COLORS = 6;

function useMaxVisibleColors(): number {
  const [maxVisible, setMaxVisible] = useState(DESKTOP_MAX_COLORS);

  React.useEffect(() => {
    const mql = globalThis.matchMedia('(max-width: 480px)');
    const update = () => setMaxVisible(mql.matches ? MOBILE_MAX_COLORS : DESKTOP_MAX_COLORS);
    update();
    mql.addEventListener('change', update);
    return () => mql.removeEventListener('change', update);
  }, []);

  return maxVisible;
}

interface ProductGridCardProps {
  product: CollectionProduct;
  currencySymbol: string;
  productUrl: string;
}

function ProductGridCard({ product, currencySymbol, productUrl }: Readonly<ProductGridCardProps>) {
  const hasColors = (product.colorOptions?.length ?? 0) > 1;
  const [selectedColor, setSelectedColor] = useState<string | undefined>(
    hasColors ? product.colorOptions![0].name : undefined,
  );
  const maxVisible = useMaxVisibleColors();

  const displayImage = useCallback(() => {
    if (!selectedColor || !product.colorOptions) return product.imageUri;
    const match = product.colorOptions.find((c) => c.name === selectedColor);
    return match?.imageUri || product.imageUri;
  }, [selectedColor, product.colorOptions, product.imageUri]);

  const imageUri = displayImage();
  const { dollars, cents } = splitPrice(product.priceMin);
  const topBadge = getTopBadgeLabel(product.tags);
  const bottomBadge = getBottomBadgeLabel(product.tags);

  const hasComparePrice = product.priceMax > product.priceMin;
  const comparePrice = hasComparePrice
    ? `${currencySymbol}${product.priceMax.toFixed(2)}`
    : null;

  const installmentPrice = getInstallmentPrice(product.priceMin, 4);

  return (
    <CardLink to={productUrl}>
      <ImageContainer>
        {imageUri ? (
          <ProductImage src={imageUri} alt={product.title} loading="lazy" />
        ) : (
          <PlaceholderImage>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </PlaceholderImage>
        )}

        {topBadge && <TopBadge>{topBadge}</TopBadge>}
        {bottomBadge && <BottomBadge>{bottomBadge}</BottomBadge>}
      </ImageContainer>

      <ContentArea>
        <ProductName>{product.title}</ProductName>

        <PriceRow>
          {comparePrice && <ComparePrice>{comparePrice}</ComparePrice>}
          <SalePrice $hasCompare={!!comparePrice}>
            {currencySymbol}{dollars}<sup>{cents}</sup>
          </SalePrice>
        </PriceRow>

        {hasComparePrice && (
          <BNPLRow>
            Or 4 payments of ${installmentPrice}
          </BNPLRow>
        )}

        <RatingRow>
          <StarsRow><StarIcons rating={STANDARD_RATING} /></StarsRow>
          <ReviewCount>{STANDARD_REVIEW_COUNT.toLocaleString()}</ReviewCount>
        </RatingRow>

        {hasColors && (
          <ColorSelectorRow>
            <ColorSelector
              colors={product.colorOptions!}
              selectedColor={selectedColor}
              onColorChange={setSelectedColor}
              maxVisible={maxVisible}
              size="sm"
            />
          </ColorSelectorRow>
        )}
      </ContentArea>
    </CardLink>
  );
}

/* ---------- component ---------------------------------------------------- */

interface ProductGridProps {
  products: CollectionProduct[];
  loading?: boolean;
}

export function ProductGrid({ products, loading }: Readonly<ProductGridProps>) {
  const { countryConfig, localizedPath } = useIntl();

  if (loading && products.length === 0) {
    return (
      <Grid>
        {Array.from({ length: 9 }, (_, i) => `skel-${i}`).map((id) => (
          <SkeletonCard key={id}>
            <SkeletonPulse style={{ width: '100%', aspectRatio: '3/4', borderRadius: 8 }} />
            <div style={{ padding: '10px 0', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <SkeletonPulse style={{ height: 14, marginBottom: 8, width: '75%' }} />
              <SkeletonPulse style={{ height: 14, marginBottom: 8, width: '50%' }} />
              <SkeletonPulse style={{ height: 22, width: '35%' }} />
            </div>
          </SkeletonCard>
        ))}
      </Grid>
    );
  }

  if (products.length === 0) {
    return (
      <EmptyState>
        <EmptyIcon><PackageSearch size={40} /></EmptyIcon>
        <EmptyTitle>No products found</EmptyTitle>
        <EmptyDescription>Try adjusting your filters or search terms.</EmptyDescription>
      </EmptyState>
    );
  }

  const currencySymbol = countryConfig.currency.format;

  return (
    <Grid>
      {products.map((product) => (
        <ProductGridCard
          key={product.id}
          product={product}
          currencySymbol={currencySymbol}
          productUrl={localizedPath(`/products/${product.slug || product.id}`)}
        />
      ))}
    </Grid>
  );
}

export default ProductGrid;
